import Parse from "parse"

const PackagingExecution = Parse.Object.extend("PackagingExecutions")

import { parseLimitRequest, dateToTimestamp } from "../../utils"
import { sortPEByTimeFields } from "../../utils/productionStepExecution"

import { PE_STATUSES } from "../../utils/packagingExecutions"
import { packagingCardOrder } from "../../utils/packaging"

export const getFilteredPackagingExecutions = async ({ filters, include = [], select = [], toJson = true }) => {
	try {
		const packagingExecutionQuery = new Parse.Query(PackagingExecution)
			.notEqualTo("deleted", true)

		if (include && include.length) {
			packagingExecutionQuery.include(include)
		}
		if (select && select.length) {
			packagingExecutionQuery.select(select)
		}
		if (filters && filters.packagingDate) {
			packagingExecutionQuery.equalTo("packagingDate", filters.packagingDate)
		}
		if (filters && filters.status && Array.isArray(filters.status)) {
			packagingExecutionQuery.containedIn("status", filters.status)
		}
		const packagingExecutions = await packagingExecutionQuery
			.ascending("theoreticalStartTime")
			.addAscending("startTime")
			.addAscending("endTime")
			.limit(parseLimitRequest)
			.find() || []

		const sortedResults = sortPEByTimeFields(packagingExecutions)

		return toJson ? sortedResults.map(packagingExecution => packagingExecution.toJSON()) : sortedResults

	}
	catch (e) {
		console.error(e.message)
	}
}

export const getPackagingExecutionById = async ({ id, toJson = true, select = [], include = [] }) => {
	try {
		const packagingExecutionQuery = new Parse.Query(PackagingExecution)
			.notEqualTo("deleted", true)
			.equalTo("objectId", id)

		if (include && include.length) {
			packagingExecutionQuery.include(include)
		}
		if (select && select.length) {
			packagingExecutionQuery.select(select)
		}
		const packagingExecution = await packagingExecutionQuery.first() || {}

		return toJson ? packagingExecution.toJSON() : packagingExecution
	}
	catch (e) {
		console.error(e.message)
	}
}

export const savePackagingExecutionNewStatus = async (packagingExecution, newStatus) => {
	try {
		const currentStatus = packagingExecution.get("status")
		if (currentStatus === newStatus) {
			return packagingExecution
		}
		// add other validation if need be
		const forbiddenStatusError = "Changement de status interdit"
		if (currentStatus === PE_STATUSES.toValidate && newStatus !== PE_STATUSES.todo) {
			throw new Error(forbiddenStatusError)
		}
		if (currentStatus === PE_STATUSES.locked && newStatus !== PE_STATUSES.toValidate) {
			throw new Error(forbiddenStatusError)
		}
		if (currentStatus === PE_STATUSES.todo && newStatus !== PE_STATUSES.inProgress) {
			throw new Error(forbiddenStatusError)
		}
		if (currentStatus === PE_STATUSES.waste && newStatus !== PE_STATUSES.done) {
			throw new Error(forbiddenStatusError)
		}
		packagingExecution.set("status", newStatus)
		const updatedPE = await packagingExecution.save()
		return updatedPE
	}
	catch (e) {
		console.error(e.message)
	}
}

/**
 * Updates start and end times for each packaging object within a PackagingExecution.
 *
 * Handles all progression cases:
 *  - If PE status is "TODO":
 *      → Finds the first packaging (in `packagingCardOrder`) with a non-zero theoretical number
 *        and sets its `startTime` to now.
 *
 *  - If PE status is "IN_PROGRESS":
 *      → Sets `endTime` for the current packaging type.
 *      → If the next step is not "packagingsCounting", sets `startTime` for the next
 *        available packaging type (with a non-zero theoretical number).
 *
 *  - If nextStep is "packagingsCounting":
 *      → Sets only the `endTime` for the current packaging type.
 *
 * After updating times, the modified PackagingExecution is saved to the database.
 */
export const savePackagingExecutionPackagingTimes = async (packagingExecution, currentStep, nextStep, PEStatus) => {
	try {
		const packagings = packagingExecution.get("packagings") || []
		const now = dateToTimestamp(new Date())

		if (PEStatus === PE_STATUSES.todo) {
			// PE just starting — find first packaging to start (with a theoretical number > 0)
			const firstType = packagingCardOrder.find(type => {
				const packaging = packagings.find(packaging => packaging.type === type)
				return packaging && packaging.theoreticalNumber > 0
			})
			if (firstType) {
				const packagingToUpdate = packagings.find(packaging => packaging.type === firstType)
				if (packagingToUpdate && !packagingToUpdate.startTime) packagingToUpdate.startTime = now
			}
		}

		else if (PEStatus === PE_STATUSES.inProgress && packagingCardOrder.includes(currentStep)) {
			// Set endTime for currentStep
			const currentPackaging = packagings.find(packaging => packaging.type === currentStep)
			if (currentPackaging && !currentPackaging.endTime) currentPackaging.endTime = now

			// Set startTime for the next packaging step (if not packagingsCounting)
			if (nextStep?.value !== PE_STATUSES.packagingsCounting) {
				const nextToDo = packagingCardOrder.find(type => {
					const packaging = packagings.find(packaging => packaging.type === type)
					return packaging && packaging.theoreticalNumber > 0 && type !== currentStep && !packaging.startTime
				})
				if (nextToDo) {
					const nextPackaging = packagings.find(packaging => packaging.type === nextToDo)
					if (nextPackaging && !nextPackaging.startTime)  nextPackaging.startTime = now
				}
			}
		}

		else if (nextStep?.value === PE_STATUSES.packagingsCounting) {
			// Final case: we just want to close the currentPackaging step
			const currentPackaging = packagings.find(packaging => packaging.type === currentStep)
			if (currentPackaging && !currentPackaging.endTime) currentPackaging.endTime = now
		}

			// Save back the modified packagings
			packagingExecution.set("packagings", packagings)
			const updatedPE = await packagingExecution.save()

			return updatedPE
		}
	catch (e) {
		console.error(e.message)
	}
}